import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, People as PeopleIcon } from '@mui/icons-material';
import { PatientFormData } from '../types/patient.types';
import { PatientService } from '../services/patient.service';
import { generateMRN } from '../../../utils/mrnGenerator';

// Validation schema - using PatientFormData directly
const patientSchema = yup.object({
  mrn: yup.string().optional(),
  fullName: yup.string().required('Full name is required'),
  gender: yup.string().required('Gender is required'),
  dob: yup.string().required('Date of birth is required'),
  phoneNumber: yup.string().optional(),
  email: yup.string().email('Invalid email format').optional(),
  address: yup.string().optional(),
  note: yup.string().optional(),
  isFamily: yup.boolean().optional(),
  familyId: yup.string().optional(),
  isActive: yup.boolean().optional(),
});

interface PatientFormProps {
  isEdit?: boolean;
}

const PatientForm: React.FC<PatientFormProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [familyPatient, setFamilyPatient] = useState<any>(null);
  const [checkingPhone, setCheckingPhone] = useState<boolean>(false);
  const [phoneCheckTimeout, setPhoneCheckTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    existingPatient?: { id: string; mrn: string; fullName: string; phoneNumber: string; age?: string; gender?: string; email?: string };
  }>({ open: false });
  
  const [nameConflictDialog, setNameConflictDialog] = useState<{
    open: boolean;
    conflictName?: string;
  }>({ open: false });

  const defaultValues = useMemo<PatientFormData>(
    () => ({
      mrn: generateMRN(), // Generate sequential MRN for new patients
      fullName: '',
      age: '',
      gender: 'MALE', // Set a default gender to avoid undefined
      dob: '',
      phoneNumber: '',
      email: '',
      address: '',
      note: '',
      isFamily: false,
      familyId: '',
      isActive: true,
    }),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: yupResolver(patientSchema) as any,
    defaultValues: defaultValues,
  });

  const watchDob = watch('dob');
  const watchPhoneNumber = watch('phoneNumber');

  // Calculate detailed age breakdown for display
  const getDetailedAgeBreakdown = (dob: string): string => {
    if (!dob) return '';
    
    // Parse DOB as local date (YYYY-MM-DD format)
    const dobParts = dob.split('-');
    const birthDate = new Date(parseInt(dobParts[0]), parseInt(dobParts[1]) - 1, parseInt(dobParts[2]));
    
    // Get today's date in local timezone
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    console.log('Age calculation debug (timezone-aware):', {
      dobString: dob,
      dobParts,
      birthDate: birthDate.toDateString(),
      todayLocal: todayLocal.toDateString(),
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isValidDate: !isNaN(birthDate.getTime())
    });
    
    let years = todayLocal.getFullYear() - birthDate.getFullYear();
    let months = todayLocal.getMonth() - birthDate.getMonth();
    let days = todayLocal.getDate() - birthDate.getDate();
    
    if (days < 0) {
      months--;
      const lastMonth = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 0);
      days += lastMonth.getDate();
    }
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    const parts = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    
    if (parts.length === 0) return 'Born today';
    
    return `Exact age: ${parts.join(', ')}`;
  };

  // Calculate age from DOB
  const calculateAge = (dob: string): string => {
    if (!dob) return '';
    
    // Parse DOB as local date (YYYY-MM-DD format)
    const dobParts = dob.split('-');
    const birthDate = new Date(parseInt(dobParts[0]), parseInt(dobParts[1]) - 1, parseInt(dobParts[2]));
    
    // Get today's date in local timezone
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    let years = todayLocal.getFullYear() - birthDate.getFullYear();
    let months = todayLocal.getMonth() - birthDate.getMonth();
    let days = todayLocal.getDate() - birthDate.getDate();
    
    if (days < 0) {
      months--;
      const lastMonth = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 0);
      days += lastMonth.getDate();
    }
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    // Format age with detailed breakdown
    const ageParts = [];
    
    if (years > 0) {
      ageParts.push(years === 1 ? '1 year' : `${years} years`);
    }
    
    if (months > 0) {
      ageParts.push(months === 1 ? '1 month' : `${months} months`);
    }
    
    if (days > 0) {
      ageParts.push(days === 1 ? '1 day' : `${days} days`);
    }
    
    if (ageParts.length === 0) {
      return 'Newborn';
    }
    
    // Join parts with commas and "and" for the last part
    if (ageParts.length === 1) {
      return ageParts[0];
    } else if (ageParts.length === 2) {
      return ageParts.join(' and ');
    } else {
      return ageParts.slice(0, -1).join(', ') + ', and ' + ageParts[ageParts.length - 1];
    }
  };

  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (watchDob) {
      const calculatedAge = calculateAge(watchDob);
      setValue('age', calculatedAge);
    }
  }, [watchDob, setValue]);

  // Check for existing patient with phone number
  useEffect(() => {
    const checkPhoneNumber = async () => {
      if (watchPhoneNumber && watchPhoneNumber.length >= 10 && !isEdit) {
        setCheckingPhone(true);
        try {
          const response = await PatientService.getPatients({ search: watchPhoneNumber, limit: 1 });
          if (response.data.length > 0) {
            const existingPatient = response.data[0];
            setFamilyPatient(existingPatient);
            setValue('isFamily', true);
            setValue('familyId', existingPatient.id);
          } else {
            setFamilyPatient(null);
            setValue('isFamily', false);
            setValue('familyId', '');
          }
        } catch (error) {
          console.error('Error checking phone number:', error);
        } finally {
          setCheckingPhone(false);
        }
      } else {
        setFamilyPatient(null);
        setValue('isFamily', false);
        setValue('familyId', '');
      }
    };

    const timeoutId = setTimeout(checkPhoneNumber, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [watchPhoneNumber, setValue, isEdit]);

  // Fetch patient data in edit mode or set default values for new patient
  useEffect(() => {
    if (isEdit && id) {
      const fetchPatient = async () => {
        try {
          setLoading(true);
          const patient = await PatientService.getPatientById(id);
          
          // Format DOB for date input (YYYY-MM-DD)
          let formattedDob = '';
          if (patient.dob) {
            // If DOB is in ISO format, extract just the date part
            if (patient.dob.includes('T')) {
              formattedDob = patient.dob.split('T')[0];
            } else {
              formattedDob = patient.dob;
            }
          }
          
          reset({
            ...patient,
            // Ensure all required fields are set with proper defaults
            mrn: patient.mrn || generateMRN(),
            fullName: patient.fullName || '',
            age: patient.age || '',
            gender: patient.gender || 'MALE', // Ensure gender is never undefined
            dob: formattedDob,
            phoneNumber: patient.phoneNumber || '',
            email: patient.email || '',
            address: patient.address || '',
            note: patient.note || '',
            isFamily: patient.isFamily || false,
            familyId: patient.familyId || '',
            isActive: patient.isActive !== undefined ? patient.isActive : true,
          });
        } catch (error) {
          console.error('Error fetching patient:', error);
          setError('Failed to load patient data');
        } finally {
          setLoading(false);
        }
      };

      fetchPatient();
    } else {
      // Set default values when in create mode
      reset(defaultValues);
    }
  }, [isEdit, id, reset, defaultValues]);

  const formatValidationErrors = (errors: Record<string, any>): string => {
    return Object.entries(errors)
      .map(([field, messages]) => {
        if (Array.isArray(messages)) {
          return `${field}: ${messages.join(', ')}`;
        }
        return `${field}: ${messages}`;
      })
      .join('\n');
  };

  // Check phone number for duplicates
  const checkPhoneNumber = async (phoneNumber: string) => {
    if (!phoneNumber || phoneNumber.length < 10) return;
    
    try {
      setCheckingPhone(true);
      const existingPatients = await PatientService.checkPhoneExists(phoneNumber);
      if (existingPatients.length > 0) {
        // Find the parent account (isFamily = false)
        const parentPatient = existingPatients.find(patient => !patient.isFamily);
        if (parentPatient) {
          setDuplicateDialog({
            open: true,
            existingPatient: {
              id: parentPatient.id,
              mrn: parentPatient.mrn,
              fullName: parentPatient.fullName,
              phoneNumber: parentPatient.phoneNumber || '',
              age: parentPatient.age?.toString() || '',
              gender: parentPatient.gender,
              email: parentPatient.email,
            }
          });
        }
      }
    } catch (error) {
      console.error('Error checking phone number:', error);
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleDuplicatePhoneNumber = (existingPatient: { id: string; mrn: string; fullName: string; phoneNumber: string; age?: string; gender?: string; email?: string }) => {
    console.log('handleDuplicatePhoneNumber called with:', existingPatient);
    setDuplicateDialog({
      open: true,
      existingPatient
    });
    console.log('Dialog state set to open');
  };

  const handleLinkAsFamily = async () => {
    const existingPatient = duplicateDialog.existingPatient!;
    
    // Set form fields to link as family member but don't create patient yet
    setValue('isFamily', true);
    setValue('familyId', existingPatient.id);
    
    // Close dialog and let user continue filling form
    setDuplicateDialog({ open: false });
    setError(null);
    
    // Show success message that linking is set up
    console.log('Form configured for family linking. Patient will be created when form is submitted.');
  };

  const handleDontLinkAsFamily = () => {
    // Clear phone number and close dialog
    setValue('phoneNumber', '');
    setDuplicateDialog({ open: false });
    setError(null);
  };

  const onSubmit = async (formData: PatientFormData) => {
    console.log('=== FORM SUBMISSION STARTED ===');
    console.log('Form data received:', formData);
    
    try {
      setSubmitting(true);
      setError(null);
      
      console.log('Preparing to submit patient data:', formData);
      console.log('Current date for validation:', new Date().toISOString());

      // Enhanced validation before submission
      const validationErrors: string[] = [];

      // Debug: Check what fields we have
      console.log('Form validation check:', {
        fullName: formData.fullName,
        fullNameTrimmed: formData.fullName?.trim(),
        gender: formData.gender,
        dob: formData.dob,
        age: formData.age
      });

      // Required field validation
      if (!formData.fullName?.trim()) {
        validationErrors.push('Full name is required');
      } else {
        // Name format validation
        const nameRegex = /^[a-zA-Z\s\.\-']+$/;
        if (!nameRegex.test(formData.fullName.trim())) {
          validationErrors.push('Full name can only contain letters, spaces, dots, hyphens, and apostrophes');
        }
        
        // Check minimum length
        if (formData.fullName.trim().length < 2) {
          validationErrors.push('Full name must be at least 2 characters long');
        }
        
        // Check maximum length
        if (formData.fullName.trim().length > 100) {
          validationErrors.push('Full name cannot exceed 100 characters');
        }
      }

      if (!formData.gender) {
        validationErrors.push('Gender is required');
      }

      // DOB validation - DOB is now required since we removed age field
      if (!formData.dob?.trim()) {
        validationErrors.push('Date of birth is required');
      }

      // Phone number validation (if provided)
      if (formData.phoneNumber?.trim()) {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        if (!phoneRegex.test(formData.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
          validationErrors.push('Please enter a valid phone number');
        }
      }

      // Email validation (if provided)
      if (formData.email?.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          validationErrors.push('Please enter a valid email address');
        }
      }


      // Date of birth validation (if provided)
      if (formData.dob) {
        // Parse DOB as local date (YYYY-MM-DD format from HTML date input)
        const dobParts = formData.dob.split('-');
        const dobDate = new Date(parseInt(dobParts[0]), parseInt(dobParts[1]) - 1, parseInt(dobParts[2]));
        
        // Get today's date in local timezone
        const today = new Date();
        const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        console.log('DOB validation (timezone-aware):', {
          dobString: formData.dob,
          dobParts,
          dobDate: dobDate.toDateString(),
          dobISO: dobDate.toISOString(),
          today: today.toDateString(),
          todayLocal: todayLocal.toDateString(),
          todayISO: today.toISOString(),
          browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          isFuture: dobDate > todayLocal
        });
        
        if (dobDate > todayLocal) {
          validationErrors.push('Date of birth cannot be in the future');
        }
        
        // Check if DOB is too far in the past (more than 150 years)
        const maxAgeDate = new Date();
        maxAgeDate.setFullYear(maxAgeDate.getFullYear() - 150);
        const maxAgeDateLocal = new Date(maxAgeDate.getFullYear(), maxAgeDate.getMonth(), maxAgeDate.getDate());
        
        if (dobDate < maxAgeDateLocal) {
          validationErrors.push('Date of birth seems too far in the past');
        }
      }

      // Family member validation
      if (formData.isFamily && !formData.familyId) {
        validationErrors.push('Family ID is required when marking as family member');
      }

      // MRN format validation (if manually entered)
      if (formData.mrn?.trim()) {
        const mrnRegex = /^MRN-?\d+$/;
        if (!mrnRegex.test(formData.mrn.trim())) {
          validationErrors.push('MRN must be in the format MRN-XXXXXX or MRNXXXXXX where X is a digit');
        }
      }

      // Address length validation (if provided)
      if (formData.address?.trim() && formData.address.trim().length > 500) {
        validationErrors.push('Address cannot exceed 500 characters');
      }

      // Note length validation (if provided)
      if (formData.note?.trim() && formData.note.trim().length > 1000) {
        validationErrors.push('Note cannot exceed 1000 characters');
      }

      // If there are validation errors, show them and stop submission
      if (validationErrors.length > 0) {
        console.log('Validation errors found:', validationErrors);
        console.log('Form data that failed validation:', formData);
        
        const errorMessage = validationErrors.length === 1 
          ? `Validation Error: ${validationErrors[0]}`
          : `Please fix the following ${validationErrors.length} validation errors:\n\n${validationErrors.map((error, index) => `${index + 1}. ${error}`).join('\n')}`;
        
        setError(errorMessage);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Prepare the data to send
      const patientData: PatientFormData = {
        ...formData,
        fullName: formData.fullName.trim(),
        // Calculate age from DOB for backend
        age: formData.dob ? calculateAge(formData.dob) : undefined,
        // Convert empty strings to undefined for optional fields
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        address: formData.address?.trim() || undefined,
        note: formData.note?.trim() || undefined,
        familyId: formData.familyId || undefined,
      };

        console.log('Submitting patient data:', patientData);
        console.log('Phone number being sent:', patientData.phoneNumber);

        let result;
        if (isEdit && id) {
          console.log('Updating existing patient with ID:', id);
          result = await PatientService.updatePatient(id, patientData);
        } else {
          console.log('Creating new patient');
          result = await PatientService.createPatient(patientData);
        }

        console.log('Patient operation successful:', result);
        
        // Show success message
        const successMessage = isEdit 
          ? `Patient "${patientData.fullName}" has been updated successfully!`
          : `Patient "${patientData.fullName}" has been created successfully!`;
        
        // You can add a toast notification here if you have a toast system
        console.log(successMessage);
        
        navigate('/patient-management/patients');
    } catch (err: any) {
      console.error('Error submitting patient:', err);
      
      // Check for family member name conflict
      if (err.message.includes('family member with the name') && err.message.includes('already exists within this family')) {
        const nameMatch = err.message.match(/name "([^"]+)"/);
        if (nameMatch) {
          setSubmitting(false);
          setNameConflictDialog({
            open: true,
            conflictName: nameMatch[1]
          });
          return;
        }
      }
      
      // Check for specific error patterns and handle them
      if (err.message.includes('phone number') && err.message.includes('already exists')) {
        // Parse existing patient info from error message
        const mrnMatch = err.message.match(/MRN:\s*([^,]+)/);
        const nameMatch = err.message.match(/Name:\s*([^,]+)/);
        const ageMatch = err.message.match(/Age:\s*([^,]+)/);
        const genderMatch = err.message.match(/Gender:\s*([^,]+)/);
        const emailMatch = err.message.match(/Email:\s*([^)]+)/);
        const phoneMatch = err.message.match(/phone number\s*([\d\s\+\-\(\)]+)/);
        
        console.log('Parsed matches:', { mrnMatch, nameMatch, phoneMatch, ageMatch, genderMatch, emailMatch });
        
        if (mrnMatch && nameMatch) {
          console.log('Showing duplicate dialog...');
          // Force stop the form submission
          setSubmitting(false);
          handleDuplicatePhoneNumber({
            id: '', // We don't have ID from error message, will be handled in dialog
            mrn: mrnMatch[1].trim(),
            fullName: nameMatch[1].trim(),
            phoneNumber: phoneMatch ? phoneMatch[1].trim() : formData.phoneNumber || '',
            age: ageMatch ? ageMatch[1].trim() : 'N/A',
            gender: genderMatch ? genderMatch[1].trim() : 'N/A',
            email: emailMatch ? emailMatch[1].trim() : 'N/A',
          });
          return;
        } else {
          console.log('Could not parse patient info from error message');
        }
      }
      
      setError(err.message || 'An error occurred while saving the patient');
      setSubmitting(false);
      
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Patients
      </Button>

      <Card>
        <CardHeader
          title={isEdit ? 'Edit Patient' : 'Add New Patient'}
          titleTypographyProps={{ variant: 'h5' }}
        />
        <Divider />
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                {/* MRN field hidden - auto-generated by backend */}
              
              <Grid item xs={12} md={6}>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      error={!!errors.fullName}
                      helperText={errors.fullName?.message}
                      disabled={loading}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="dob"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Date of Birth *"
                      type="date"
                      variant="outlined"
                      error={!!errors.dob}
                      helperText={errors.dob?.message || (watchDob ? getDetailedAgeBreakdown(watchDob) : "Age will be calculated automatically")}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required
                    />
                  )}
                />
              </Grid>


              <Grid item xs={12} md={6}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.gender}>
                      <InputLabel id="gender-label">Gender *</InputLabel>
                      <Select
                        {...field}
                        labelId="gender-label"
                        label="Gender *"
                      >
                        <MenuItem value="MALE">Male</MenuItem>
                        <MenuItem value="FEMALE">Female</MenuItem>
                        <MenuItem value="OTHER">Other</MenuItem>
                      </Select>
                      {errors.gender && (
                        <FormHelperText>{errors.gender.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                      onBlur={(e) => {
                        field.onBlur();
                        // Clear any existing timeout
                        if (phoneCheckTimeout) {
                          clearTimeout(phoneCheckTimeout);
                        }
                        // Set a new timeout to check phone number after user stops typing
                        const timeout = setTimeout(() => {
                          checkPhoneNumber(e.target.value);
                        }, 500);
                        setPhoneCheckTimeout(timeout);
                      }}
                    />
                  )}
                />
                {checkingPhone && (
                  <Typography variant="caption" color="info.main">
                    Checking for existing patient...
                  </Typography>
                )}
                {familyPatient && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PeopleIcon fontSize="small" />
                      <Typography variant="body2">
                        This phone number belongs to <strong>{familyPatient.fullName}</strong> (MRN: {familyPatient.mrn}).
                        This patient will be marked as a family member.
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Address"
                      multiline
                      rows={3}
                      variant="outlined"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      variant="outlined"
                      error={!!errors.note}
                      helperText={errors.note?.message}
                    />
                  )}
                />
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Typography color="error">{error}</Typography>
                </Grid>
              )}

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/patient-management/patients')}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={submitting}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                  >
                    {isEdit ? 'Update' : 'Save'} Patient
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Family Member Linking Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false })}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          Phone Number Already Exists
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A patient with phone number <strong>{duplicateDialog.existingPatient?.phoneNumber}</strong> already exists:
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Name:</strong> {duplicateDialog.existingPatient?.fullName}
            </Typography>
            <Typography variant="body2">
              <strong>MRN:</strong> {duplicateDialog.existingPatient?.mrn}
            </Typography>
            <Typography variant="body2">
              <strong>Age:</strong> {duplicateDialog.existingPatient?.age || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Gender:</strong> {duplicateDialog.existingPatient?.gender || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {duplicateDialog.existingPatient?.email || 'N/A'}
            </Typography>
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            Would you like to link this new patient as a family member, or use a different phone number?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDontLinkAsFamily}
            color="secondary"
          >
            Use Different Phone Number
          </Button>
          <Button 
            onClick={handleLinkAsFamily}
            variant="contained"
            color="primary"
          >
            Link as Family Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Name Conflict Dialog */}
      <Dialog
        open={nameConflictDialog.open}
        onClose={() => setNameConflictDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Name Already Exists in Family
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The name <strong>"{nameConflictDialog.conflictName}"</strong> already exists within this family. 
            Each family member must have a unique name.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Please choose a different name or add a middle name/initial to make it unique.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNameConflictDialog({ open: false })}
            variant="contained"
            color="primary"
          >
            OK, I'll Change the Name
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientForm;
